import { useState } from 'react';
import { Button } from '@carbon/react';
import { Download } from '@carbon/icons-react';
import ErrorToast from 'components/error-toast';
import { api } from 'lib/request/api';
import { downloadBlob } from 'utils/download-blob';
import type { SlotValuationInputsResponse } from 'types/packages-api/slot-valuation-inputs';
import {
  buildTearsheetExport,
  type ExportMetaByNode,
} from './tearsheet-export';
import { TEARSHEET_REQUEST_TIMEOUT_MS } from './hooks';
import { useNodeMeta } from './tearsheet-sections';

/**
 * ── ONE BUTTON, AT THE TOP ───────────────────────────────────────────────────────────────
 *
 * UAT item 11. Andrew settled this against a download control per accordion: one button for
 * the whole tearsheet, because the workbook is the whole tearsheet — every block flat on one
 * sheet with the monthly series behind it, not the section you happened to have open.
 *
 * ── IT POSTS THE ROWS, NOT THE IDENTIFIERS ───────────────────────────────────────────────
 *
 * The body carries the rows this page just rendered, built by `buildTearsheetExport` from the
 * same functions the table renders from. The server writes them and decides nothing. That is
 * what makes the file unable to disagree with the page; see SlotTearsheetExporter for why the
 * writing happens server-side at all (frozen panes).
 *
 * @module
 */

export const EXPORT_URL = 'queries/packages/slot_valuation_inputs/exports';

/** Shown while the workbook is being built, and as the button's resting label. */
export const EXPORT_LABEL = 'Export to Excel';
export const EXPORT_BUSY_LABEL = 'Preparing…';
export const EXPORT_ERROR = 'The export could not be built. Try again.';
/** Why the button is disabled when the model map failed terminally (see below). */
export const EXPORT_MAP_UNAVAILABLE =
  'Export needs the model map, which could not load. Reopen the tearsheet to try again.';

/**
 * The filename the server chose, read from Content-Disposition.
 *
 * The server owns the name — it follows Eval3ExportFilename, so a tearsheet's file sorts
 * beside the slots and cashflows exports for the same evaluation. Rebuilding it here would be
 * a second naming scheme that drifts from the first.
 *
 * @param {(string|null)} disposition - The response header.
 * @return {string} The filename, or a plain fallback if the header is missing.
 */
export function filenameFrom(disposition: string | null): string {
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : 'slot_tearsheet.xlsx';
}

/**
 * A plain-text subtitle for the workbook's second line.
 *
 * Deliberately not the page's <Subtitle>, which is JSX carrying links — a spreadsheet cell
 * cannot hold an anchor, and the row addresses live in the Link column where a reader can
 * actually use them.
 *
 * @param {SlotValuationInputsResponse} response - The payload.
 * @return {string} The subtitle line.
 */
export function exportSubtitle(response: SlotValuationInputsResponse): string {
  const { slot } = response.header;
  return [
    slot.wellName,
    slot.unit?.name,
    [slot.basin, slot.tier].filter(Boolean).join(' ') || null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The tearsheet's export control.
 *
 * @param {object} props - The payload and the slot's identifiers.
 * @return {JSX.Element} The button.
 */
export function TearsheetExportButton({
  response,
  packageId,
  calculationId,
  slotId,
}: {
  response: SlotValuationInputsResponse | undefined;
  packageId: number | string | null;
  calculationId: number | string | null;
  slotId: string | null;
}) {
  const { byKey: meta, resolved, failed: metaFailed } = useNodeMeta();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // ── DISABLED UNTIL THE MODEL MAP HAS LANDED, NOT JUST THE PAYLOAD ────────────────────
  //
  // `meta` supplies every row's unit, source and reserve category. Exporting before it
  // arrives produces a workbook that is not wrong so much as QUIETLY THINNER than the page —
  // no units, no sources, and the reserve-category rule failing open so twelve rows the page
  // removed come back. A file that silently disagrees with the screen is the one outcome
  // this feature must not have.
  //
  // When the map fails TERMINALLY (metaFailed) the button therefore stays disabled — but it
  // now SAYS SO through the title below, rather than sitting dead with no explanation, and
  // the table view carries the same notice.
  const ready = Boolean(response) && resolved;

  // ── WHY NOT lib/hooks/useBlobDownload (Jason, PR #7465) ───────────────────────────────
  //
  // It is the right shape on paper — POST, read the blob, take the server's filename off
  // Content-Disposition, hand it to downloadBlob — and pages/packages/new/steps uses it
  // twice. It is the wrong fit here, for two reasons that are behavioural rather than
  // stylistic, and a second implementation is the correct answer when the difference is real.
  //
  //   1. IT CANNOT CARRY A TIMEOUT. It calls bare `fetch`, which has none, while this POST
  //      needs the 120s tearsheet ceiling for the reason above — the one thing this run's
  //      sibling commit exists to fix. Adopting the hook would undo it.
  //
  //   2. THE BODY IS BUILT AT CLICK TIME, NOT AT RENDER TIME. `body` is a hook option
  //      captured in a useCallback dependency list, so the ~530 KB export payload — which
  //      `buildTearsheetExport` produces by walking the registry's edges and regex-scanning
  //      formula text — would be rebuilt on every render of this component rather than once
  //      per click.
  //
  // A third, smaller: the hook surfaces `errorBody.error` as the message, and this
  // endpoint's error is an OBJECT (`{ code, message }`), so it would read "[object Object]";
  // and this button deliberately does not show the server's reason at all.
  //
  // Recorded here rather than left as an unexplained divergence, so the next reader does not
  // have to re-derive it — and so that if useBlobDownload grows a timeout and a lazy body,
  // this becomes a one-line substitution.
  const onClick = async () => {
    if (!response || busy) {
      return;
    }
    setBusy(true);
    setFailed(false);
    try {
      const body = buildTearsheetExport(response, meta as ExportMetaByNode);
      const httpResponse = await api.post(EXPORT_URL, {
        // ── THE SAME CEILING AS THE READS, FOR A STRONGER REASON (Jason, PR #7465) ──────
        //
        // Without this the POST inherits ky's 30s default (lib/request/api.ts:20) while both
        // tearsheet READS opt into 120s (hooks.ts:227, :277). The implication is exact: a
        // slot slow enough to need the long read is a slot whose export is slower still —
        // the body is the rendered row set, larger than the response the 120s budget was
        // chosen for, and the server then builds the workbook synchronously. So the export
        // aborted on precisely the slots that most needed it, and failed as a timeout the
        // user could not distinguish from anything else.
        timeout: TEARSHEET_REQUEST_TIMEOUT_MS,
        json: {
          packageId,
          calculationId,
          slotId,
          title: `Slot valuation inputs — ${slotId ?? ''}`.trim(),
          subtitle: exportSubtitle(response),
          ...body,
        },
      });
      downloadBlob(
        await httpResponse.blob(),
        filenameFrom(httpResponse.headers.get('content-disposition'))
      );
    } catch {
      // The reason is on the response and is deliberately not surfaced verbatim — it can
      // name internals. The button says the action failed and stays clickable.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  // ── A FAILURE HAS TO BE VISIBLE, NOT JUST HOVERABLE (Jason, PR #7465) ─────────────────
  //
  // This announced failure through a `title` tooltip alone. A tooltip is invisible to a
  // screen reader, unreachable by keyboard, and absent on touch — so for those users a click
  // that failed was indistinguishable from a click that did nothing, and the reasonable
  // conclusion is that the export worked. For an audit document whose entire purpose is
  // being trustworthy, silently not producing one is the worst available outcome.
  //
  // ErrorToast is the pattern this directory already uses for exactly this — content-export.tsx
  // renders it for a background export failure — and it portals a Carbon ToastNotification,
  // which carries its own live-region semantics. Not a fifth way of doing this.
  //
  // `timeout={0}` so it stands until dismissed: a failed export is a standing fact the user
  // has to act on, not a transient notice. The reason is deliberately NOT surfaced verbatim;
  // it can name internals.
  return (
    <>
      {failed && (
        <ErrorToast
          message={EXPORT_ERROR}
          timeout={0}
          onClose={() => {
            setFailed(false);
            return true;
          }}
        />
      )}
      <Button
        kind="ghost"
        size="sm"
        renderIcon={Download}
        disabled={!ready || busy}
        onClick={onClick}
        title={
          failed
            ? EXPORT_ERROR
            : metaFailed
              ? EXPORT_MAP_UNAVAILABLE
              : undefined
        }
      >
        {busy ? EXPORT_BUSY_LABEL : EXPORT_LABEL}
      </Button>
    </>
  );
}
